/**
 * The audio converter and the audio joiner (SEO_BRIEF_11 section 2.1): /audio-converter and
 * /audio-joiner, on the /mp4-to-mp3 engine (src/media/audio-convert.ts, src/media/audio-join.ts).
 *
 * Tool rows like any other, so the hub, the sitemap and the link graph find them, rendered by
 * `tools/audio_convert.html` (server/app/routes_pages.py `AUDIO_CONVERT_SLUGS`) because nothing
 * on either page is a subtitle file:
 *
 *   audio-converter   one audio or video file's sound saved as MP3 (128, 192 or 320 kbit/s) or
 *                     WAV. Those two are its only outputs: the encoder writes nothing else.
 *   audio-joiner      two or more files, in the visitor's order, joined into one MP3 with no
 *                     gap, a 1 or 2 second gap, or a short crossfade.
 *
 * `input: 'video'` as on /mp4-to-mp3: the drop zone takes a video as well as audio, and the row
 * transcribes nothing, so it counts among the tools that run in the browser
 * (`runs_in_browser` in server/app/tools.py).
 *
 * THE READ FORMATS are the files the engine opens and the dropzone routes to it, checked with
 * a scripted run on real files: MP3, M4A, WAV, OGG (Vorbis or Opus), Opus, FLAC and AAC (ADTS),
 * and the sound of MP4, MOV, MKV, WebM and M4V videos. AIFF, WMA and AMR are not read. Whether a
 * codec plays is the browser's: a file it cannot decode gets the page's "can't play" banner.
 *
 * THE FOUR FORMAT PAGES (SEO_BRIEF_12 section 3): /m4a-to-mp3, /m4a-to-wav, /mp3-to-wav and
 * /wav-to-mp3 are the converter opened with its input and output preset, one row each, so the
 * hub, the menus, the sitemap and the stats list find them as they find the converter. A row
 * names its one input in `read_formats` and its one output in `write_formats`; the template
 * reads the preset from those two (routes_pages.AUDIO_FORMAT_SLUGS), and the page still converts
 * any audio file it is given, as the converter does. Never `from_format`/`to_format`: those make
 * a subtitle pair page (tools/pair.html).
 */

import type { OptionsSchema } from '../transforms/clean/schema.ts';
import { VIDEO_READS } from './audio-out-tools.ts';

/** The audio files the drop zone takes, in the order the page names them. */
export const AUDIO_READS = ['mp3', 'm4a', 'wav', 'ogg', 'opus', 'flac', 'aac'] as const;

/** Everything the two pages read: the audio files, then the videos /mp4-to-mp3 names. */
export const CONVERT_READS = [...AUDIO_READS, ...VIDEO_READS] as const;

/** The converter saves MP3 or WAV; tools/audio_convert.html keys its convert mode on this. */
export const CONVERTER_WRITES = ['mp3', 'wav'] as const;

/** The joiner saves one MP3. */
export const JOINER_WRITES = ['mp3'] as const;

export interface AudioConvertRow {
  readonly slug: string;
  readonly label: string;
  readonly op: 'convert';
  readonly ops: readonly string[];
  readonly options_schema: OptionsSchema;
  readonly read_formats: readonly string[];
  readonly write_formats: readonly string[];
  readonly input: 'video';
}

/** The page draws its own controls (format, gap): the shared options panel has none. */
const NO_OPTIONS: OptionsSchema = { version: 1, groups: [] };
const NO_OPS: readonly string[] = [];

/** One input the engine reads, and one output the converter writes. */
type FormatIn = (typeof AUDIO_READS)[number];
type FormatOut = (typeof CONVERTER_WRITES)[number];

/** A format page's row: the converter, presetting one file type in and one out. */
function formatPage(slug: string, label: string, from: FormatIn, to: FormatOut): AudioConvertRow {
  return {
    slug,
    label,
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: [from],
    write_formats: [to],
    input: 'video',
  };
}

export const AUDIO_CONVERT_TOOLS: readonly AudioConvertRow[] = [
  {
    slug: 'audio-converter',
    label: 'Audio converter',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: CONVERT_READS,
    write_formats: CONVERTER_WRITES,
    input: 'video',
  },
  {
    slug: 'audio-joiner',
    label: 'Audio joiner',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: CONVERT_READS,
    write_formats: JOINER_WRITES,
    input: 'video',
  },
  // SEO_BRIEF_12 section 3, in the brief's order: the converter with its preset.
  formatPage('m4a-to-mp3', 'M4A to MP3', 'm4a', 'mp3'),
  formatPage('m4a-to-wav', 'M4A to WAV', 'm4a', 'wav'),
  formatPage('mp3-to-wav', 'MP3 to WAV', 'mp3', 'wav'),
  formatPage('wav-to-mp3', 'WAV to MP3', 'wav', 'mp3'),
];
